import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type BookFromPageResult, type OpenBookingPageResult } from '@whinerp/client';
import { formatDate, formatDateTime } from '@whinerp/contracts/format';
import { Button } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-81 A public booking page (G-187): the owner's free times, a name and an
 * email, and a confirmation with a calendar file. A time that went while the
 * visitor was choosing is said, and the times shown again with what they
 * typed kept.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

function BookingPageScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const params = useParams();
  const workspace = params['workspace'] ?? '';
  const slug = params['slug'] ?? '';
  const [chosen, setChosen] = useState<string | null>(null);
  const [details, setDetails] = useState({ name: '', email: '', note: '' });

  const page = useQuery<OpenBookingPageResult, ApiError>({
    queryKey: ['booking-page', workspace, slug],
    queryFn: () => api.openBookingPage({ path: { workspace, slug } }),
  });
  const book = useMutation<BookFromPageResult, ApiError>({
    mutationFn: () =>
      api.bookFromPage({
        path: { workspace, slug },
        body: { startsAt: chosen!, name: details.name, email: details.email, note: details.note || null },
      }),
    onSuccess: (result) => {
      if (!result.booked) {
        setChosen(null);
        void page.refetch();
      }
    },
  });

  if (page.isPending) return <BookingPageLoading />;
  if (page.isError || !page.data.page) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-2 px-gutter py-6">
        <h1 data-booking-page-missing className="text-h2 text-ink">{t('bookingPage.missing')}</h1>
      </main>
    );
  }
  const view = page.data.page;
  const outcome = book.data;

  if (outcome?.booked) {
    // The API's own address, as the rest of the app reaches it.
    const base = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';
    return (
      <main data-component="BookingPage" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-3 px-gutter py-6">
        <h1 className="text-h1 text-ink">{t('bookingPage.booked')}</h1>
        <p data-booking-confirmed className="text-body text-ink">
          {t('bookingPage.bookedWith', { values: { title: view.title, owner: view.ownerName, when: formatDateTime(outcome.startsAt ?? '', format) } })}
        </p>
        {outcome.calendarToken ? (
          <a data-booking-calendar className="text-body text-bronze underline" href={`${base}/ics/${encodeURIComponent(outcome.calendarToken)}`}>
            {t('bookingPage.addToCalendar')}
          </a>
        ) : null}
      </main>
    );
  }

  // Grouped by the visitor's own day, since that is how they think about it.
  const days = new Map<string, typeof view.slots>();
  for (const slot of view.slots) {
    const day = formatDate(slot.startsAt, format);
    days.set(day, [...(days.get(day) ?? []), slot]);
  }

  return (
    <main data-component="BookingPage" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-4 px-gutter py-6">
      <p className="text-meta text-ink-meta">{view.ownerName}</p>
      <h1 className="text-h1 text-ink">{view.title}</h1>
      {view.description ? <p className="whitespace-pre-wrap text-body text-ink">{view.description}</p> : null}
      {outcome && !outcome.booked ? (
        <p data-booking-refused={outcome.reason} className="text-dense text-warning-text">
          {t(`bookingPage.refused.${outcome.reason ?? 'missing'}` as 'bookingPage.refused.taken')}
        </p>
      ) : null}
      {view.slots.length === 0 ? (
        <p data-booking-no-slots className="text-body text-ink-meta">{t('bookingPage.noSlots')}</p>
      ) : (
        <section className="flex flex-col gap-3" data-booking-slots>
          {[...days].map(([day, slots]) => (
            <div key={day} className="flex flex-col gap-1">
              <h2 className="text-dense text-ink-600">{day}</h2>
              <div className="flex flex-wrap gap-2">
                {slots.map((slot) => (
                  <Button key={slot.startsAt} size="small" intent={chosen === slot.startsAt ? 'primary' : 'secondary'}
                    data-booking-slot={slot.startsAt} aria-pressed={chosen === slot.startsAt} onClick={() => setChosen(slot.startsAt)}>
                    {new Date(slot.startsAt).toLocaleTimeString(format.locale, { hour: '2-digit', minute: '2-digit' })}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
      {chosen ? (
        <form data-booking-details className="flex flex-col gap-3 rounded-card border border-rule p-4"
          onSubmit={(event) => {
            event.preventDefault();
            book.mutate();
          }}>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('bookingPage.name')}
            <input className={INPUT} required data-booking-name value={details.name} onChange={(event) => setDetails({ ...details, name: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('bookingPage.email')}
            <input className={INPUT} required type="email" data-booking-email value={details.email} onChange={(event) => setDetails({ ...details, email: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('bookingPage.note')}
            <textarea className="rounded-control border border-control bg-surface p-2 text-body text-ink" data-booking-note value={details.note}
              onChange={(event) => setDetails({ ...details, note: event.target.value })} />
          </label>
          {book.error ? <p className="text-dense text-danger-text">{errorText(book.error)}</p> : null}
          <div>
            <Button intent="primary" type="submit" data-booking-confirm disabled={book.isPending}>{t('bookingPage.confirm')}</Button>
          </div>
        </form>
      ) : null}
    </main>
  );
}

function BookingPageLoading(): JSX.Element {
  const t = useT();
  return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
}
function BookingPageMissing(): JSX.Element {
  const t = useT();
  return <p className="p-6 text-body text-ink-meta">{t('bookingPage.missing')}</p>;
}

export const screen = defineScreen({
  id: 'SC-81',
  route: '/b/:workspace/:slug',
  surface: 'page',
  chrome: 'bare',
  title: 'Booking page',
  features: ['F-057'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: [],
  elements: ['BookingPage'],
  sampleRecord: [
    { table: { name: 'tenant', idColumn: 'slug' }, param: 'workspace' },
    { table: { name: 'booking_page', idColumn: 'slug' }, param: 'slug' },
  ],
  states: { empty: BookingPageMissing, loading: BookingPageLoading, error: BookingPageMissing },
  component: BookingPageScreen,
});
